import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navigation";
import { Typography, Button, useTheme, Tooltip } from "@material-ui/core";
import { TextField, Box, Menu, MenuItem, Modal } from "@mui/material";
import SnackbarAlert from "../components/SnackbarAlert";
import Footer from "../components/Footer";
import API_ENDPOINTS from "../utilities/APIRoutes";
import IconButton from "@mui/material/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined";
import PostAddRoundedIcon from "@mui/icons-material/PostAddRounded";
import MenuIcon from "@mui/icons-material/Menu";
import { useDropzone } from "react-dropzone";
import { AvailableCollectionsContext } from "../contexts/AvailableCollectionsContext";
import { TourContext } from "../contexts/TourContext";
import { EditOutlined } from "@mui/icons-material";
import MainChatSelectionPanel from "../components/MainChatSelectionPanel";
import ChatSidebar from "../components/ChatSidebar";
import ChatWindow from "../components/ChatWindow";
import CollectionInfoPopover from "../components/CollectionInfoPopover";
import useSnackbar from "../hooks/useSnackbar";
import useChatSessions from "../hooks/useChatSessions";
import useDataManagement from "../hooks/useDataManagement";
import useStreamingChat from "../hooks/useStreamingChat";
import RetryMessage from "../components/RetryMessage";
import { ERROR_TYPES } from "../utils/errorClassifier";
import { loadSettings } from "./UserSettings";
import useQuickChatIntro from "../hooks/useQuickChatIntro";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    padding: theme.spacing(0.2),
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Chrome, Safari, Edge
    },
    msOverflowStyle: "none", // IE
  },
  centerContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    flexGrow: 1,
    flexShrink: 0,
    fontFamily: "Raleway, sans-serif",
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
      paddingTop: theme.spacing(10),
      padding: theme.spacing(10, 2, 4),
    },
  },
  inputContainer: {
    display: "flex",
    flexDirection: "column",
    width: theme.spacing(80),
    marginBottom: theme.spacing(2),
    alignItems: "flex-center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  input: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "transparent", // Remove border by default
      },
      "&:hover fieldset": {
        borderColor: "transparent", // Remove border on hover
      },
      "&.Mui-focused fieldset": {
        borderColor: "#FFC629", // Apply custom border color when focused
      },
    },
  },
  title: {
    color: "#ffffff",
    fontSize: "2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    fontFamily: "Raleway, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem", // font size for smaller screens
    },
  },
  description: {
    color: "#ffffff",
    fontSize: "1.2rem",
    marginBottom: theme.spacing(4),
    fontFamily: "Raleway, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem", // font size for smaller screens
    },
  },
  boxContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    backgroundColor: "#000000",
    borderRadius: 14,
    padding: theme.spacing(4),
    marginBottom: theme.spacing(8),
    maxWidth: 1100,
    width: "100%",
    // flexGrow: 1,
    overflow: "hidden",
    [theme.breakpoints.down("lg")]: {
      width: "calc(100% - 32px)", // Adjust for padding
    },
    
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
  },
  boxHeading: {
    color: "#ffffff",
    fontSize: "1rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    fontFamily: "Raleway, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem", // font size for smaller screens
    },
  },
  chat: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "end",
    height: theme.spacing(70),
    // height: "100%",
    width: "-webkit-fill-available",
    borderRadius: 9,
    padding: theme.spacing(1),
  },
  text: {
    color: "#ffffff",
    fontFamily: "Raleway, sans-serif",
    fontSize: "0.9rem",
    fontWeight: "bold",
  },
  listItem: {
    justifyContent: "space-between",
    backgroundColor: "#303030",
    marginBottom: "0.9em",
    borderRadius: 8,
  },
  chatSessionText: {
    fontFamily: "Raleway",
    fontSize: "0.9em",
    fontWeight: "normal",

    padding: "0.5px 3px",
  },
  listItemCollectionText: {
    fontFamily: "Raleway, sans-serif",
    color: "black",
  },
  checkbox: {
    color: "#FFC629",
    "&.Mui-checked": {
      color: "#FFC629",
    },
  },
  resultsButton: {
    backgroundColor: "#FFC629",
    color: "#000000",
    borderRadius: "px",
    fontFamily: "Raleway, sans-serif",
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#FFC629",
      color: "#000000",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem", // font size for smaller screens
    },
  },
  button: {
    backgroundColor: "#FFC629",
    color: "#000000",
    "&:hover": {
      backgroundColor: "#FFC629",
      color: "#000000",
    },
    borderRadius: "10px",
    padding: theme.spacing(1, 3),
    fontSize: "1rem",
    fontWeight: "bold",
    textTransform: "none",
    marginTop: theme.spacing(2),
    fontFamily: "Raleway, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem", // font size for smaller screens
    },
  },
  cancelButton: {
    backgroundColor: "#D9D9D9",
    "&:hover": {
      backgroundColor: "#D9D9D9",
      color: "#000000",
    },
    borderRadius: "10px",
    padding: theme.spacing(1, 3),
    fontSize: "1rem",
    fontWeight: "bold",
    textTransform: "none",
    marginTop: theme.spacing(2),
    fontFamily: "Raleway, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem", // font size for smaller screens
    },
  },
  footerContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    backgroundColor: "#FFC629",
    padding: theme.spacing(2),
  },
  footer: {
    color: "#747171",
    fontSize: "0.8rem",
    fontFamily: "Raleway, sans-serif",
  },
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    height: "500px",
    width: "100%", // Full width within parent
    // backgroundColor: "#1a1a1a", // Background color for chat section
    borderRadius: 9,
    padding: theme.spacing(1),
    overflow: "hidden",
  },
  chatHistory: {
    flexGrow: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
    height: "400px",
    alignContent: "center",
  },
  chatInputContainer: {
    padding: theme.spacing(1),
    borderTop: "1px solid #444",
    // backgroundColor: "#1a1a1a", // Match the background of the chat
  },
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 500,
    backgroundColor: "#202020",
    borderRadius: "14px",
    padding: theme.spacing(4),
    outline: "none",
  },
  modalTitle: {
    fontFamily: "Raleway, sans-serif",
    fontWeight: "bold",
    color: "white",
    fontSize: "1.5rem",
    wordBreak: "break-word",
    marginBottom: theme.spacing(2),
  },
  navLink: {
    marginLeft: "0.8em",
    color: "#ffffff",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
      // fontWeight: "bold",
    },
  },
  activeNavLink: {
    color: "#FFC629",
    "&:hover": {
      opacity: 0.8,
    },
  },
  navLinksContainer: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
}));

function MainChat() {
  const { chatId: urlChatId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles();
  const [loadingRes, setLoadingRes] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isChatDisabled, setIsChatDisabled] = useState(false);
  const { startTour, registerCallback } = useContext(TourContext);
  const { markSeen: markQuickChatIntroSeen } = useQuickChatIntro();

  const [userQuery, setUserQuery] = useState("");
  const [chatHistory, setChatHistory] = useState("");
  const [chatId, setChatId] = useState(null);
  const lastMessageRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [tokenLimit, setTokenLimit] = useState(400000);

  const updateTotalTokensUsed = (usage) => {
    if (!usage) return;
    const tokens = usage.tokens_used || usage.total_tokens;
    if (typeof tokens === "number") {
      setTotalTokensUsed((prevTotal) => {
        const newTotal = prevTotal + tokens;
        localStorage.setItem("tokenUsage", newTotal);
        return newTotal;
      });
    }
  };


  // Add streaming state
  const [streamingEnabled, setStreamingEnabled] = useState(() => loadSettings().streaming);
  const [errorMessages, setErrorMessages] = useState({});

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    handleCloseSnackbar,
  } = useSnackbar();

  // whether to include the analysis section in DB queries
  const [includeAnalysis, setIncludeAnalysis] = useState(true);

  // CSV Stuff
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileId, setCsvFileId] = useState("");
  const [isVisualization, setIsVisualization] = useState(false);
  const [visualizationData, setVisualizationData] = useState(null);
  const [visualizationCode, setVisualizationCode] = useState(null);
  const [lastQuery, setLastQuery] = useState("");
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState("");

  const [activeChatId, setActiveChatId] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const UserToken = localStorage.getItem("userToken");
  const AuthToken = localStorage.getItem("authToken");
  const userRole = localStorage.getItem("role");

  const {
    chatSession,
    setChatSessions,
    renameModalOpen,
    setRenameModalOpen,
    newChatName,
    setNewChatName,
    handleRenameChat,
    anchorEl,
    handleMenuOpen,
    handleMenuClose,
    openRenameModal,
    closeRenameModal,
    selectedChatForMenu,
    setSelectedChatForMenu,
    fetchChatSessions,
  } = useChatSessions(AuthToken, UserToken, showSnackbar);

  const {
    loading,
    setLoading,
    selectionType,
    collections,
    databases,
    selectedCollections,
    setSelectedCollections,
    pendingDatabaseId,
    setPendingDatabaseId,
    selectedDatabase,
    setSelectedDatabase,
    availableCollections,
    handleSelectionTypeChange,
    handleSelectionTypeChangeWithoutReset,
    handleCollectionChange,
    handleDatabaseChange,
    collectionFilesMap,
    fetchFiles,
  } = useDataManagement(
    AuthToken,
    UserToken,
    showSnackbar,
    updateTotalTokensUsed
  );

  // Add streaming hook
  const {
    isStreaming,
    streamingMessageId,
    streamChat,
    stopStreaming,
    retryRequest,
    retryAttempt,
  } = useStreamingChat({
    showSnackbar,
    updateTokenUsage: (tokenUsage) => {
      // Simplified version - no need for tokenUsageProcessed check
      if (tokenUsage && tokenUsage.tokens_used) {
        const tokensUsed = tokenUsage.tokens_used;
        let currentTokenUsage =
          parseInt(localStorage.getItem("tokenUsage")) || 0;
        currentTokenUsage += tokensUsed;
        localStorage.setItem("tokenUsage", currentTokenUsage);
        setTotalTokensUsed(currentTokenUsage);
      }
    },
  });

  // Add this function to handle retry for a specific message
  const handleRetry = async () => {
    setErrorMessages({});
    await retryRequest();
  };

  // Add this function to handle cancelling a retry
  const handleCancelRetry = () => {
    setErrorMessages({});
    // Remove the error message from the last bot message
    setChatHistory((prevHistory) => {
      const lastBotMessageIndex = [...prevHistory]
        .reverse()
        .findIndex((msg) => msg.sender === "bot");

      if (lastBotMessageIndex >= 0) {
        const actualIndex = prevHistory.length - 1 - lastBotMessageIndex;
        const updatedHistory = [...prevHistory];
        updatedHistory[actualIndex] = {
          ...updatedHistory[actualIndex],
          hasError: false,
          showRetry: false,
        };
        return updatedHistory;
      }
      return prevHistory;
    });
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
    handleMenuClose();
  };

  // CSV Stuff
  const { getRootProps: csvGetRootProps, getInputProps: csvGetInputProps } =
    useDropzone({
      accept: {
        "text/csv": [".csv"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
      },
      multiple: false,
      onDrop: ([file]) => setCsvFile(file),
    });

  const handleCsvUpload = async () => {
    if (!csvFile) {
      showSnackbar("Please choose a CSV or XLSX file first", "error");
      return false;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);

      const response = await fetch(API_ENDPOINTS.CSVUpload, {
        method: "POST",
        headers: {
          "authorization-token": AuthToken,
          "user-token": UserToken,
          "Chat-Id": chatId || "",
        },
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");
      const { file_id } = await response.json();
      setCsvFileId(file_id);
      showSnackbar("CSV uploaded! File ID: " + file_id, "success");
      return true;
    } catch (err) {
      console.error(err);
      showSnackbar("Error uploading CSV", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCsvFile = () => {
    setCsvFile(null);
    setCsvFileId("");
  };

  const handleGenerateVisualization = async () => {
    if (!csvFileId || !visualizationData) return;
    setLoadingRes(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.CSVVisualization.replace("{file_id}", csvFileId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "authorization-token": AuthToken,
            "user-token": UserToken,
            "Chat-Id": chatId || "",
          },
          body: JSON.stringify({
            query: lastSubmittedQuery,
            result_data: { data: visualizationData },
          }),
        }
      );
      const result = await response.json();

      if (result.visualization_code) {
        setVisualizationCode(result.visualization_code);

        setChatHistory((prev) => [
          ...prev,
          {
            id: Date.now(), // or use UUID
            sender: "visualization",
            message: "Generated visualization 📊.",
            visualizationCode: result.visualization_code,
          },
        ]);
      } else {
        showSnackbar("Error generating visualization", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err.message, "error");
    } finally {
      setLoadingRes(false);
    }
  };

  useEffect(() => {
    // Only fetch chat messages if we have both urlChatId and collections are loaded
    if (urlChatId && collections.length > 0) {
      fetchChatMessages(urlChatId);
    }
    const initialTokenUsage = parseInt(localStorage.getItem("tokenUsage")) || 0;
    const tokenLimit = parseInt(localStorage.getItem("tokenLimit"));

    setTotalTokensUsed(initialTokenUsage);
    setTokenLimit(tokenLimit);
  }, [urlChatId, collections]);

  // Register sidebar callbacks for the tour
  useEffect(() => {
    registerCallback("openSidebar", () => setSidebarOpen(true));
    registerCallback("closeSidebar", () => setSidebarOpen(false));
    registerCallback("markQuickChatIntroSeen", () => markQuickChatIntroSeen());
  }, [registerCallback, markQuickChatIntroSeen]);

  // Start main chat tour on first visit
  useEffect(() => {
    const timer = setTimeout(() => {
      startTour("mainChat");
    }, 800);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      selectionType === "databases" &&
      pendingDatabaseId &&
      databases.length > 0
    ) {
      const matchingDatabase = databases.find(
        (db) => db.id === pendingDatabaseId
      );
      if (matchingDatabase) {
        setSelectedDatabase(matchingDatabase);
        setPendingDatabaseId(null);
      }
    }
  }, [
    databases,
    pendingDatabaseId,
    selectionType,
    setSelectedDatabase,
    setPendingDatabaseId,
  ]);

  const CSV_COLLECTION_ID = "81359f82-5f63-4bef-8920-48be3d264efb"; /// This is the ID for the CSV collection (temporary)

  const fetchChatMessages = async (chatId) => {
    try {
      setActiveChatId(chatId);
      setLoading(true);
      setIsChatDisabled(false);
      console.log("Fetching chat messages chatId:", chatId);
      // Fetch chat messages with citations from the server
      const [chatResponse, historyResponse] = await Promise.all([
        fetch(API_ENDPOINTS.RetrieveChat, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "User-Token": UserToken,
            Authorization: AuthToken,
          },
          body: JSON.stringify({ chatID: chatId }),
        }),
        fetch(`${API_ENDPOINTS.RetrieveChatHistory}?chat_id=${chatId}`, {
          method: "GET",
          headers: {
            "authorization-token": AuthToken,
            "user-token": UserToken,
          },
        }),
      ]);

      if (!chatResponse.ok) {
        throw new Error("Failed to retrieve chat messages");
      }

      const data = await chatResponse.json();
      console.log("Raw chat data from server:", data);

      if (data.error || !data.message) {
        showSnackbar("Error retrieving chat messages", "error");
        return;
      }

      // Use the new citation-aware endpoint if it returned messages, otherwise fall back to legacy
      let formattedMessages;
      let usedCitationEndpoint = false;
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        console.log("Chat history with citations:", historyData);
        const messages = historyData.messages || [];
        if (messages.length > 0) {
          usedCitationEndpoint = true;
          formattedMessages = messages.map((msg) => ({
            sender: msg.sender === "user" ? "user" : "bot",
            message: msg.message,
            ...(msg.citations ? { citations: msg.citations } : {}),
          }));
        }
      }
      if (!usedCitationEndpoint) {
        console.warn("Citation history endpoint returned no messages, falling back to legacy");
        const messages = data.message.messages || [];
        const sortedMessages = messages.sort((a, b) => a.createdAt - b.createdAt);
        formattedMessages = sortedMessages.map((msg) => {
          const source = msg.source || (msg.metadata && msg.metadata.source);
          return {
            id: msg.id,
            sender: source === "user" ? "user" : "bot",
            message: msg.message,
          };
        });
      }

      console.log("Chat Sessions:", data);

      // setChatHistory(formattedMessages);
      setChatHistory((prevHistory) => {
        // If server has no messages but we have local messages (new chat scenario)
        if (formattedMessages.length === 0 && prevHistory.length > 0) {
          return prevHistory; // Keep existing local messages
        }
        // Otherwise use server messages
        return formattedMessages;
      });
      setChatId(chatId);

      const collectionID = data.message.chat.collectionID;
      const collectionIDs = data.message.chat.collectionIDs;

      // Check if this chat uses the CSV collection ID
      const isCSVChat =
        collectionID === CSV_COLLECTION_ID ||
        (collectionIDs &&
          Array.isArray(collectionIDs) &&
          collectionIDs.includes(CSV_COLLECTION_ID));

      if (isCSVChat) {
        console.log("Detected CSV chat, switching to CSV mode");
        // This is a CSV chat, set selection to CSV and exit early
        handleSelectionTypeChangeWithoutReset({
          target: { value: "csv" },
        });
        setIsChatDisabled(false); // Ensure chat is not disabled for CSV
        return; // Exit early to prevent collection/database logic from running
      }
      // === END CSV DETECTION LOGIC ===

      let sourceFound = false;

      if (
        collectionIDs &&
        Array.isArray(collectionIDs) &&
        collectionIDs.length > 0
      ) {
        // First check if this is a database chat
        const matchingDatabase = databases.find((database) =>
          collectionIDs.includes(database.id)
        );

        if (matchingDatabase) {
          // This is a database chat
          setPendingDatabaseId(matchingDatabase.id);
          handleSelectionTypeChangeWithoutReset({
            target: { value: "databases" },
          });
          sourceFound = true;
        } else {
          // Try to find matching collections
          const matchingCollections = collections.filter((collection) =>
            collectionIDs.includes(collection.id)
          );

          if (matchingCollections.length > 0) {
            setSelectedCollections(matchingCollections);
            fetchFiles(matchingCollections.map((c) => c.id));
            handleSelectionTypeChangeWithoutReset({
              target: { value: "collections" },
            });
            sourceFound = true;
          }
        }
      } else if (collectionID) {
        // Handle single collection or database
        const matchingCollection = collections.find(
          (collection) => collection.id === collectionID
        );
        const matchingDatabase = databases.find(
          (database) => database.id === collectionID
        );
        if (matchingCollection) {
          setSelectedCollections([matchingCollection]);
          fetchFiles([matchingCollection.id]);
          handleSelectionTypeChangeWithoutReset({
            target: { value: "collections" },
          });
          sourceFound = true;
        } else if (matchingDatabase) {
          setPendingDatabaseId(collectionID);
          handleSelectionTypeChangeWithoutReset({
            target: { value: "databases" },
          });
          sourceFound = true;
        }
      }

      const hasCollectionInfo =
        collectionID || (collectionIDs && collectionIDs.length > 0);

      if (hasCollectionInfo && !sourceFound) {
        showSnackbar(
          "The collection or database for this chat no longer exists.",
          "error"
        );
        setIsChatDisabled(true);
        setSelectedCollections([]);
        setSelectedDatabase(null);
      }
    } catch (error) {
      console.error("Error fetching chat messages:", error);
      showSnackbar("Error fetching chat messages", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChatSelection = (chatId) => {
    // Update the URL with the selected chatId
    navigate(`/main-chat/c/${chatId}`);
    fetchChatMessages(chatId); // Fetch messages for the selected chat
  };

  const handleNewChat = () => {
    setChatId(null);
    setChatHistory([]);
    setSelectedCollections([]);
    setSelectedDatabase("");
    setActiveChatId(null);
    setIsChatDisabled(false);
    showSnackbar("New chat started!", "success");
    navigate("/main-chat");
  };

  const createChat = async (
    collectionId,
    collectionNames,
    collectionids = []
  ) => {
    const chatName = "Untitled";
    try {
      const response = await fetch(API_ENDPOINTS.CreateChatCollections, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: AuthToken,
          "User-Token": UserToken,
        },
        body: JSON.stringify({
          chatName: chatName,
          collectionID: collectionId,
          collectionIDs: collectionids,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Backend generates the real chat name asynchronously (~10s).
        // Refetch once so the sidebar picks it up without a page reload.
        setTimeout(fetchChatSessions, 10000);
        return { id: data.message.id, chatName: data.message.chatName };
      } else {
        console.error("Failed to create chat");
        return null;
      }
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  };

  const getChatResponse = async (
    chatId,
    collectionNames,
    query,
    chatHistory = [],
    chatName,
    streaming = false // Add streaming parameter
  ) => {
    try {
      console.log("Chat name sent to GetChatResponse:", chatName);
      console.log("Chat ID sent to GetChatResponse:", chatId);

      // If streaming is enabled, use the streamChat function
      if (streaming) {
        const botMessage = {
          id: Date.now().toString(),
          sender: "bot",
          message: "",
          isStreaming: true,
          streamingSupported: true,
        };

        setChatHistory((prevHistory) => [...prevHistory, botMessage]);

        const response = await streamChat({
          endpoint: API_ENDPOINTS.GetChatResponse,
          headers: {
            "Content-Type": "application/json",
            "authorization-token": AuthToken,
            "user-token": UserToken,
          },
          body: {
            chat_id: chatId,
            collection_names: collectionNames,
            query: query,
            chat_history: chatHistory.length > 0 ? chatHistory : undefined,
            chat_name: chatName,
            stream_timeout: 30,
            user_settings: loadSettings(),
          },
          onStart: (messageId) => {
            setChatHistory((prevHistory) => {
              const lastIndex = prevHistory.length - 1;
              if (lastIndex >= 0 && prevHistory[lastIndex].sender === "bot") {
                const updatedHistory = [...prevHistory];
                updatedHistory[lastIndex] = {
                  ...updatedHistory[lastIndex],
                  id: messageId,
                };
                return updatedHistory;
              }
              return prevHistory;
            });
          },
          onChunk: (chunk) => {
            if (chunk.type === "status") {
              // Pipeline status update — add/update step in pipelineSteps
              setChatHistory((prevHistory) => {
                const lastIndex = prevHistory.length - 1;
                if (lastIndex >= 0 && prevHistory[lastIndex].sender === "bot") {
                  const updatedHistory = [...prevHistory];
                  const currentMsg = updatedHistory[lastIndex];
                  const prevSteps = currentMsg.pipelineSteps || [];

                  // Mark all previous steps as completed, add/update the new active step
                  const newSteps = prevSteps.map((s) =>
                    s.status === "active" ? { ...s, status: "completed" } : s
                  );
                  // Check if this step already exists (update message) or is new
                  const existingIdx = newSteps.findIndex(
                    (s) => s.step === chunk.data.step
                  );
                  if (existingIdx >= 0) {
                    newSteps[existingIdx] = {
                      ...newSteps[existingIdx],
                      message: chunk.data.message,
                      status: "active",
                    };
                  } else {
                    newSteps.push({
                      step: chunk.data.step,
                      message: chunk.data.message,
                      status: "active",
                    });
                  }

                  updatedHistory[lastIndex] = {
                    ...currentMsg,
                    pipelineSteps: newSteps,
                  };
                  return updatedHistory;
                }
                return prevHistory;
              });
            } else if (chunk.type === "content") {
              const replaceFull = chunk.data.metadata && chunk.data.metadata.replaces_full_response;
              setChatHistory((prevHistory) => {
                const lastIndex = prevHistory.length - 1;
                if (lastIndex >= 0 && prevHistory[lastIndex].sender === "bot") {
                  const updatedHistory = [...prevHistory];
                  const currentMsg = updatedHistory[lastIndex];

                  // On first content chunk, mark all pipeline steps as completed
                  let pipelineSteps = currentMsg.pipelineSteps;
                  let pipelineComplete = currentMsg.pipelineComplete;
                  if (pipelineSteps && !pipelineComplete) {
                    pipelineSteps = pipelineSteps.map((s) => ({
                      ...s,
                      status: "completed",
                    }));
                    pipelineComplete = true;
                  }

                  updatedHistory[lastIndex] = {
                    ...currentMsg,
                    message: replaceFull
                      ? chunk.data.content
                      : currentMsg.message + chunk.data.content,
                    pipelineSteps,
                    pipelineComplete,
                  };
                  return updatedHistory;
                }
                return prevHistory;
              });
            } else if (chunk.type === "metadata") {
              console.log("[Citations Debug] metadata chunk:", JSON.stringify(chunk.data.citations, null, 2));
              setChatHistory((prevHistory) => {
                const lastIndex = prevHistory.length - 1;
                if (lastIndex >= 0 && prevHistory[lastIndex].sender === "bot") {
                  const updatedHistory = [...prevHistory];
                  const currentMessage = updatedHistory[lastIndex];
                  updatedHistory[lastIndex] = {
                    ...currentMessage,
                    ...(chunk.data || {}),
                  };
                  return updatedHistory;
                }
                return prevHistory;
              });
            } else if (chunk.type === "token_usage") {
              if (chunk.data && chunk.data.total_tokens) {
                const tokensUsed = chunk.data.total_tokens;
                let currentTokenUsage =
                  parseInt(localStorage.getItem("tokenUsage")) || 0;
                currentTokenUsage += tokensUsed;
                localStorage.setItem("tokenUsage", currentTokenUsage);
                setTotalTokensUsed(currentTokenUsage);
              }
            } else if (chunk.type === "error") {
              showSnackbar(chunk.data.message || "Error in stream", "error");
            }
          },
          onComplete: (finalResponse, messageId) => {
            // Ensure finalResponse is not null or undefined
            finalResponse = finalResponse || {};

            setChatHistory((prevHistory) => {
              return prevHistory.map((msg) => {
                if (msg.id === messageId) {
                  // Check if finalResponse has the expected structure
                  const messageContent =
                    finalResponse.message ||
                    (finalResponse.data && finalResponse.data.message) ||
                    msg.message ||
                    ""; // Ensure message is never undefined

                  const finalSources =
                    finalResponse.sources ||
                    (finalResponse.data && finalResponse.data.sources);

                  const finalCitations =
                    finalResponse.citations ||
                    (finalResponse.data && finalResponse.data.citations);

                  return {
                    ...msg,
                    isStreaming: false,
                    message: messageContent,
                    // Add sources if available
                    sources: finalSources || msg.sources,
                    // Add citations if available
                    citations: finalCitations || msg.citations,
                  };
                }
                return msg;
              });
            });

            // Check for chat_name in different possible locations
            const chatName =
              finalResponse.chat_name ||
              (finalResponse.data && finalResponse.data.chat_name);

            if (chatName) {
              setChatSessions((prevSessions) =>
                prevSessions.map((chat) =>
                  chat.id === chatId ? { ...chat, chatName: chatName } : chat
                )
              );
            }
          },
          onError: (error) => {
            if (error.type === ERROR_TYPES.NETWORK) {
              setErrorMessages({
                message: error.message,
                type: error.type,
                retryable: error.retryable,
              });

              setChatHistory((prevHistory) => {
                // Find the message with the streaming ID
                const messageIndex = prevHistory.findIndex(
                  (msg) => msg.id === streamingMessageId
                );
                if (messageIndex >= 0) {
                  const updatedHistory = [...prevHistory];
                  updatedHistory[messageIndex] = {
                    ...updatedHistory[messageIndex],
                    isStreaming: false,
                    hasError: true,
                    showRetry: true,
                  };
                  return updatedHistory;
                }
                return prevHistory;
              });
            } else if (error.type === ERROR_TYPES.SERVER) {
              setChatHistory((prevHistory) => {
                // Find the message with the streaming ID
                const messageIndex = prevHistory.findIndex(
                  (msg) => msg.id === streamingMessageId
                );
                if (messageIndex >= 0) {
                  const updatedHistory = [...prevHistory];
                  updatedHistory[messageIndex] = {
                    ...updatedHistory[messageIndex],
                    isStreaming: false,
                    hasError: true,
                    message:
                      updatedHistory[messageIndex].message || error.message,
                  };
                  return updatedHistory;
                }
                return prevHistory;
              });
            }

            setLoadingRes(false);
          },
        });

        return response;
      } else {
        // Traditional non-streaming approach
        const response = await fetch(API_ENDPOINTS.GetChatResponse, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "authorization-token": AuthToken,
            "user-token": UserToken,
          },
          body: JSON.stringify({
            chat_id: chatId,
            collection_names: collectionNames,
            query: query,
            chat_history: chatHistory.length > 0 ? chatHistory : undefined,
            chat_name: chatName,
            user_settings: loadSettings(),
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const chatResponse = data.data;

          if (
            data &&
            data.data.token_usage &&
            data.data.token_usage.tokens_used
          ) {
            const tokensUsed = data.data.token_usage.tokens_used;
            let currentTokenUsage =
              parseInt(localStorage.getItem("tokenUsage")) || 0;
            currentTokenUsage += tokensUsed;
            localStorage.setItem("tokenUsage", currentTokenUsage);
            setTotalTokensUsed(currentTokenUsage);
          }

          return chatResponse;
        } else {
          console.error("Failed to get chat response");
          return null;
        }
      }
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  };

  // Update the handleSubmitQuery function
  const handleSubmitQuery = async () => {
    if (selectionType === "collections" && selectedCollections.length === 0) {
      showSnackbar(
        "Please select a collection before sending a query!",
        "error"
      );
      return;
    }

    if (selectionType === "databases" && !selectedDatabase) {
      showSnackbar("Please select a database before sending a query!", "error");
      return;
    }

    if (userQuery.trim() === "") return;

    // Clear any previous error messages
    setErrorMessages({});

    const submittedQuery = userQuery;
    setUserQuery("");
    setLoadingRes(true);

    const userMessage = { sender: "user", message: submittedQuery };
    const loadingPlaceholder = !streamingEnabled
      ? { sender: "bot", message: "", isLoading: true }
      : null;
    setChatHistory((prevHistory) => [
      ...prevHistory,
      userMessage,
      ...(loadingPlaceholder ? [loadingPlaceholder] : []),
    ]);

    setLastSubmittedQuery(submittedQuery);

    try {
      let response;

      if (selectionType === "collections") {
        // Handle query for collections
        const collectionIds = selectedCollections.map(
          (collection) => collection.id
        );
        const collectionNames = selectedCollections.map(
          (collection) => collection.name
        );

        const currentChatHistory = [...chatHistory, userMessage]; // Snapshot of updated history

        let currentChatId = chatId; // Use existing chatId if available
        let chatInfo;
        if (!chatId) {
          const singleCollectionId =
            collectionIds.length > 0 ? collectionIds[0] : null;

          chatInfo = await createChat(
            singleCollectionId,
            collectionNames,
            collectionIds
          );
          if (chatInfo) {
            currentChatId = chatInfo.id;
            setChatId(chatInfo.id);
            navigate(`/main-chat/c/${chatInfo.id}`);

            // Manually add the new chat to the local state
            setChatSessions((prevSessions) => [
              { ...chatInfo, createdAt: new Date().toISOString() },
              ...prevSessions,
            ]);
          }
        }

        if (currentChatId) {
          const currentChat = chatSession.find(
            (chat) => chat.id === currentChatId
          );

          // Use streaming for collections
          response = await getChatResponse(
            currentChatId,
            collectionNames,
            submittedQuery,
            currentChatHistory,
            chatInfo ? chatInfo.chatName : currentChat?.chatName,
            streamingEnabled // Pass the streamingEnabled flag
          );

          if (response && response.chat_name) {
            setChatSessions((prevSessions) =>
              prevSessions.map((chat) =>
                chat.id === currentChatId
                  ? { ...chat, chatName: response.chat_name }
                  : chat
              )
            );
          }

          // Streaming already handles the bot message via onChunk/onComplete,
          // so skip the fallback message addition below.
          if (streamingEnabled) return;
        }
      } else if (selectionType === "databases") {
        const database = selectedDatabase;
        const databaseType = database.collectionType;

        const databaseEndpoints = {
          nosql_chat: API_ENDPOINTS.NoSQLDBChat,
          dynamodb: API_ENDPOINTS.DynamoDBChat,
          sql_chat: API_ENDPOINTS.SQLDBChat,
          csv_chat: API_ENDPOINTS.CSVChat,
        };

        const endpoint = databaseEndpoints[databaseType];
        if (!endpoint) {
          showSnackbar("Unsupported database type.", "error");
          return;
        }

        const dbChatHistory = [...chatHistory, userMessage];

        // Create a chat for the database
        let currentChatId = chatId; // Use existing chatId if available
        let chatInfo;
        if (!chatId) {
          chatInfo = await createChat(
            database.id,
            [database.name],
            [database.id] // Pass database.id as collectionIDs array
          );
          if (chatInfo) {
            currentChatId = chatInfo.id;
            setChatId(chatInfo.id);
            navigate(`/main-chat/c/${currentChatId}`);

            // Manually add the new chat to the local state
            setChatSessions((prevSessions) => [
              { ...chatInfo, createdAt: new Date().toISOString() },
              ...prevSessions,
            ]);
          }
        }

        if (currentChatId) {
          const requestBody = {
            user_query: submittedQuery,
            chat_history: dbChatHistory,
          };

          // Find the current chat from the session to get the most up-to-date name
          const currentChat = chatSession.find(
            (chat) => chat.id === currentChatId
          );

          // Determine chat name with better logic
          let chatNameToSend;
          if (currentChat) {
            // Use the name from the chat session (most reliable)
            chatNameToSend = currentChat.chatName;
          } else if (chatInfo) {
            // Fallback to chat creation info (first request only)
            chatNameToSend = chatInfo.chatName;
          }

          if (
            databaseType === "sql_chat" ||
            databaseType === "nosql_chat" ||
            databaseType === "dynamodb"
          ) {
            if (databaseType === "sql_chat") {
              requestBody.collection_id = database.id;
              requestBody.collection_name = database.name;
              requestBody.selected_tables = selectedCollections;
              requestBody.include_analysis = includeAnalysis;
            } else if (databaseType === "nosql_chat") {
              requestBody.collection_id = database.id;
              requestBody.selected_collections = selectedCollections;
              requestBody.include_analysis = includeAnalysis;
            } else if (databaseType === "dynamodb") {
              requestBody.collection_id = database.id;
              requestBody.include_analysis = includeAnalysis;
              requestBody.selected_fields = selectedCollections;
            }

            // Create an empty bot message for streaming
            if (streamingEnabled) {
              const botMessage = {
                id: Date.now().toString(),
                sender: "bot",
                message: "",
                isStreaming: true,
                streamingSupported: true,
              };

              setChatHistory((prevHistory) => [...prevHistory, botMessage]);

              // Use streaming for SQL, NoSQL, and DynamoDB chat
              response = await streamChat({
                endpoint,
                headers: {
                  "Content-Type": "application/json",
                  "authorization-token": AuthToken,
                  "user-token": UserToken,
                },
                body: {
                  ...requestBody,
                  chat_id: currentChatId,
                  chat_name: chatNameToSend,
                  streaming: true,
                  stream_timeout: 30,
                },
                onStart: (messageId) => {
                  // Update the bot message with the streaming ID
                  setChatHistory((prevHistory) => {
                    const lastIndex = prevHistory.length - 1;
                    if (
                      lastIndex >= 0 &&
                      prevHistory[lastIndex].sender === "bot"
                    ) {
                      const updatedHistory = [...prevHistory];
                      updatedHistory[lastIndex] = {
                        ...updatedHistory[lastIndex],
                        id: messageId,
                      };
                      return updatedHistory;
                    }
                    return prevHistory;
                  });
                },
                onChunk: (chunk) => {
                  // Handle structured chunks
                  if (chunk.type === "content") {
                    // Update the message content with the chunk content
                    setChatHistory((prevHistory) => {
                      const lastIndex = prevHistory.length - 1;
                      if (
                        lastIndex >= 0 &&
                        prevHistory[lastIndex].sender === "bot"
                      ) {
                        const updatedHistory = [...prevHistory];
                        updatedHistory[lastIndex] = {
                          ...updatedHistory[lastIndex],
                          message:
                            updatedHistory[lastIndex].message +
                            chunk.data.content,
                        };
                        return updatedHistory;
                      }
                      return prevHistory;
                    });
                  } else if (chunk.type === "metadata") {
                    // Handle metadata chunk with query results
                    if (chunk.data.query_results) {
                      setChatHistory((prevHistory) => {
                        const lastIndex = prevHistory.length - 1;
                        if (
                          lastIndex >= 0 &&
                          prevHistory[lastIndex].sender === "bot"
                        ) {
                          const updatedHistory = [...prevHistory];
                          updatedHistory[lastIndex] = {
                            ...updatedHistory[lastIndex],
                            queryResults: chunk.data.query_results,
                            showResults: false, // default hidden
                          };
                          return updatedHistory;
                        }
                        return prevHistory;
                      });
                    }
                  } else if (chunk.type === "token_usage") {
                    // Handle token usage updates without displaying in chat
                    if (chunk.data && chunk.data.total_tokens) {
                      const tokensUsed = chunk.data.total_tokens;
                      let currentTokenUsage =
                        parseInt(localStorage.getItem("tokenUsage")) || 0;
                      currentTokenUsage += tokensUsed;
                      localStorage.setItem("tokenUsage", currentTokenUsage);
                      setTotalTokensUsed(currentTokenUsage);
                    }
                  } else if (chunk.type === "error") {
                    // Handle error chunks
                    showSnackbar(
                      chunk.data.message || "Error in stream",
                      "error"
                    );
                  }
                },
                onComplete: (finalResponse, messageId) => {
                  // Update the final message and mark as complete
                  setChatHistory((prevHistory) => {
                    return prevHistory.map((msg) => {
                      if (msg.id === messageId) {
                        // Check if there are query results to display
                        const hasValidResults =
                          finalResponse.query_results &&
                          Array.isArray(finalResponse.query_results) &&
                          finalResponse.query_results.length > 0;

                        return {
                          ...msg,
                          isStreaming: false,
                          message: finalResponse.response || msg.message,
                          queryResults: hasValidResults
                            ? finalResponse.query_results
                            : null,
                          showResults: false,
                        };
                      }
                      return msg;
                    });
                  });

                  // Update chat name if provided
                  if (finalResponse.chat_name) {
                    setChatSessions((prevSessions) =>
                      prevSessions.map((chat) =>
                        chat.id === currentChatId
                          ? { ...chat, chatName: finalResponse.chat_name }
                          : chat
                      )
                    );
                  }

                  setLoadingRes(false);
                },
                onError: (error) => {
                  // Handle different error types
                  if (error.type === ERROR_TYPES.NETWORK) {
                    // For network errors, show retry option
                    setErrorMessages({
                      message: error.message,
                      type: error.type,
                      retryable: error.retryable,
                    });

                    // Mark the message with an error and show retry option
                    setChatHistory((prevHistory) => {
                      return prevHistory.map((msg) => {
                        if (msg.id === streamingMessageId) {
                          return {
                            ...msg,
                            isStreaming: false,
                            hasError: true,
                            showRetry: true,
                          };
                        }
                        return msg;
                      });
                    });
                  } else if (error.type === ERROR_TYPES.SERVER) {
                    // For server errors, show error message
                    setChatHistory((prevHistory) => {
                      return prevHistory.map((msg) => {
                        if (msg.id === streamingMessageId) {
                          return {
                            ...msg,
                            isStreaming: false,
                            hasError: true,
                            message: msg.message || error.message,
                          };
                        }
                        return msg;
                      });
                    });
                  }

                  setLoadingRes(false);
                },
                streamingEnabled,
              });

              // Early return since streaming handles the response
              return;
            } else {
              // Use traditional fetch for non-streaming
              response = await fetch(endpoint, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "authorization-token": AuthToken,
                  "user-token": UserToken,
                },
                body: JSON.stringify({
                  ...requestBody,
                  chat_id: currentChatId,
                  chat_name: chatNameToSend,
                  stream_timeout: 30,
                }),
              }).then((res) => (res.ok ? res.json() : null));
            }
          } else {
            // For other database types that don't support streaming, use traditional fetch
            response = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "authorization-token": AuthToken,
                "user-token": UserToken,
              },
              body: JSON.stringify({
                ...requestBody,
                chat_id: currentChatId,
                chat_name: chatNameToSend,
                stream_timeout: 30,
              }),
            }).then((res) => (res.ok ? res.json() : null));

            // Add this code to update chat name from response
            if (response && response.chat_name) {
              setChatSessions((prevSessions) =>
                prevSessions.map((chat) =>
                  chat.id === currentChatId
                    ? { ...chat, chatName: response.chat_name }
                    : chat
                )
              );
            }
          }
        }
      } else if (selectionType === "csv") {
        if (!csvFileId) {
          showSnackbar("Upload a CSV first!", "error");
          return;
        }

        let currentChatId = chatId;
        let chatInfo;
        if (!chatId) {
          chatInfo = await createChat(
            CSV_COLLECTION_ID,
            ["CSV Analysis"],
            [CSV_COLLECTION_ID]
          );
          if (chatInfo) {
            currentChatId = chatInfo.id;
            setChatId(chatInfo.id);
            navigate(`/main-chat/c/${currentChatId}`);
            setChatSessions((prevSessions) => [
              {
                ...chatInfo,
                createdAt: new Date().toISOString(),
                isCSVChat: true,
                csvFileId: csvFileId,
              },
              ...prevSessions,
            ]);
          }
        }

        const currentChat = chatSession.find(
          (chat) => chat.id === currentChatId
        );
        let chatNameToSend;
        if (currentChat) {
          chatNameToSend = currentChat.chatName;
        } else if (chatInfo) {
          chatNameToSend = chatInfo.chatName;
        }

        const endpoint = `${API_ENDPOINTS.CSVChat.replace(
          "{file_id}",
          csvFileId
        )}`;
        const requestBody = {
          user_query: submittedQuery,
          chat_history: chatHistory,
          chat_name: chatNameToSend,
        };

        if (streamingEnabled) {
          const botMessage = {
            id: Date.now().toString(),
            sender: "bot",
            message: "",
            isStreaming: true,
            streamingSupported: true,
          };
          setChatHistory((prevHistory) => [...prevHistory, botMessage]);

          response = await streamChat({
            endpoint,
            headers: {
              "Content-Type": "application/json",
              "authorization-token": AuthToken,
              "user-token": UserToken,
              "Chat-Id": currentChatId || "",
            },
            body: {
              ...requestBody,
              streaming: true,
              stream_timeout: 30,
            },
            onStart: (messageId) => {
              setChatHistory((prevHistory) => {
                const lastIndex = prevHistory.length - 1;
                if (lastIndex >= 0 && prevHistory[lastIndex].sender === "bot") {
                  const updatedHistory = [...prevHistory];
                  updatedHistory[lastIndex] = {
                    ...updatedHistory[lastIndex],
                    id: messageId,
                  };
                  return updatedHistory;
                }
                return prevHistory;
              });
            },
            onChunk: (chunk) => {
              if (chunk.type === "content") {
                setChatHistory((prevHistory) => {
                  const lastIndex = prevHistory.length - 1;
                  if (
                    lastIndex >= 0 &&
                    prevHistory[lastIndex].sender === "bot"
                  ) {
                    const updatedHistory = [...prevHistory];
                    updatedHistory[lastIndex] = {
                      ...updatedHistory[lastIndex],
                      message:
                        updatedHistory[lastIndex].message + chunk.data.content,
                    };
                    return updatedHistory;
                  }
                  return prevHistory;
                });
              } else if (chunk.type === "metadata") {
                console.log("Processing metadata chunk:", chunk.data);
                if (chunk.data && chunk.data.visualization_code) {
                  console.log(
                    "Found visualization_code:",
                    chunk.data.visualization_code
                  );
                  setChatHistory((prevHistory) => {
                    const lastIndex = prevHistory.length - 1;
                    if (
                      lastIndex >= 0 &&
                      prevHistory[lastIndex].sender === "bot"
                    ) {
                      const updatedHistory = [...prevHistory];
                      updatedHistory[lastIndex] = {
                        ...updatedHistory[lastIndex],
                        visualizationCode: chunk.data.visualization_code,
                      };
                      return updatedHistory;
                    }
                    return prevHistory;
                  });
                }
              } else if (chunk.type === "token_usage") {
                if (chunk.data && chunk.data.total_tokens) {
                  const tokensUsed = chunk.data.total_tokens;
                  let currentTokenUsage =
                    parseInt(localStorage.getItem("tokenUsage")) || 0;
                  currentTokenUsage += tokensUsed;
                  localStorage.setItem("tokenUsage", currentTokenUsage);
                  setTotalTokensUsed(currentTokenUsage);
                }
              } else if (chunk.type === "error") {
                showSnackbar(chunk.data.message || "Error in stream", "error");
              }
            },
            onComplete: (finalResponse, messageId) => {
              finalResponse = finalResponse || {};

              setChatHistory((prev) =>
                prev.map((msg) => {
                  if (msg.id === messageId) {
                    const responseText =
                      finalResponse.response ||
                      finalResponse.result ||
                      msg.message ||
                      "";

                    const finalMessage = {
                      ...msg, // Preserve existing properties like visualizationCode
                      isStreaming: false,
                      message: responseText,
                    };

                    if (
                      finalResponse.visualization_code &&
                      !finalMessage.visualizationCode
                    ) {
                      finalMessage.visualizationCode =
                        finalResponse.visualization_code;
                    }
                    return finalMessage;
                  }
                  return msg;
                })
              );

              setIsVisualization(finalResponse.is_visualization);
              setVisualizationData(finalResponse.visualization_data);
              setLastQuery(submittedQuery);

              if (finalResponse.chat_name) {
                setChatSessions((prevSessions) =>
                  prevSessions.map((chat) =>
                    chat.id === currentChatId
                      ? { ...chat, chatName: finalResponse.chat_name }
                      : chat
                  )
                );
              }
              setLoadingRes(false);
            },
            onError: (error) => {
              if (error.type === ERROR_TYPES.NETWORK) {
                setErrorMessages({
                  message: error.message,
                  type: error.type,
                  retryable: error.retryable,
                });
                setChatHistory((prevHistory) => {
                  return prevHistory.map((msg) => {
                    if (msg.id === streamingMessageId) {
                      return {
                        ...msg,
                        isStreaming: false,
                        hasError: true,
                        showRetry: true,
                      };
                    }
                    return msg;
                  });
                });
              } else if (error.type === ERROR_TYPES.SERVER) {
                setChatHistory((prevHistory) => {
                  return prevHistory.map((msg) => {
                    if (msg.id === streamingMessageId) {
                      return {
                        ...msg,
                        isStreaming: false,
                        hasError: true,
                        message: msg.message || error.message,
                      };
                    }
                    return msg;
                  });
                });
              }
              setLoadingRes(false);
            },
          });
          return;
        } else {
          // Non-streaming CSV chat logic
          try {
            const res = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "authorization-token": AuthToken,
                "user-token": UserToken,
                "Chat-Id": currentChatId || "",
              },
              body: JSON.stringify(requestBody),
            });
            if (!res.ok) throw new Error("CSV chat error");

            const data = await res.json();

            if (data.chat_name) {
              setChatSessions((prevSessions) =>
                prevSessions.map((chat) =>
                  chat.id === currentChatId
                    ? { ...chat, chatName: data.chat_name }
                    : chat
                )
              );
            }

            if (data.token_usage && data.token_usage.total_tokens) {
              const tokensUsed = data.token_usage.total_tokens;
              let currentTokenUsage =
                parseInt(localStorage.getItem("tokenUsage")) || 0;
              currentTokenUsage += tokensUsed;
              localStorage.setItem("tokenUsage", currentTokenUsage);
              setTotalTokensUsed(currentTokenUsage);
            }

            const responseText =
              data.response || data.result || "No response received.";

            setIsVisualization(data.is_visualization);
            setVisualizationData(data.visualization_data);
            setLastQuery(submittedQuery);

            const botMessage = {
              sender: "bot",
              message: responseText,
            };

            if (data.visualization_code) {
              setVisualizationCode(data.visualization_code);
              botMessage.visualizationCode = data.visualization_code;
            }

            setChatHistory((prev) => {
              const idx = prev.findLastIndex((m) => m.isLoading);
              if (idx !== -1) {
                const updated = [...prev];
                updated[idx] = botMessage;
                return updated;
              }
              return [...prev, botMessage];
            });
          } catch (err) {
            console.error(err);
          } finally {
            setLoadingRes(false);
          }
          return;
        }
      }

      if (response) {
        // Handle token usage
        if (response.token_usage && response.token_usage.total_tokens) {
          const tokensUsed = response.token_usage.total_tokens;
          let currentTokenUsage =
            parseInt(localStorage.getItem("tokenUsage")) || 0;
          currentTokenUsage += tokensUsed;
          localStorage.setItem("tokenUsage", currentTokenUsage);
          setTotalTokensUsed(currentTokenUsage);
        }

        // Handle response for non-streaming cases
        // Replace the loading placeholder with the real response
        const replaceLoading = (botMessage) => {
          setChatHistory((prevHistory) => {
            const idx = prevHistory.findLastIndex((m) => m.isLoading);
            if (idx !== -1) {
              const updated = [...prevHistory];
              updated[idx] = botMessage;
              return updated;
            }
            return [...prevHistory, botMessage];
          });
        };

        if (response.response) {
          // Database response
          const hasValidResults =
            Array.isArray(response.query_results) &&
            response.query_results.length > 0;

          replaceLoading({
            sender: "bot",
            message: response.response,
            queryResults: hasValidResults ? response.query_results : null,
            showResults: false, // default hidden
          });
        } else {
          // Collection response
          replaceLoading({
            sender: "bot",
            message: response.message,
          });
        }
      }
    } catch (error) {
      console.error("Error in handleSubmitQuery:", error);
      // Replace loading placeholder with error message
      setChatHistory((prevHistory) => {
        const idx = prevHistory.findLastIndex((m) => m.isLoading);
        const errorMsg = {
          sender: "bot",
          message: "Sorry, I encountered an error. Please try again.",
          hasError: true,
        };
        if (idx !== -1) {
          const updated = [...prevHistory];
          updated[idx] = errorMsg;
          return updated;
        }
        return [...prevHistory, errorMsg];
      });
    } finally {
      setLoadingRes(false);
    }
  };

  const sortedCollections = [...collections].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const sortedDatabases = [...databases].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const sortedAvailableCollections = [...availableCollections].sort((a, b) =>
    a.localeCompare(b)
  );

  const isSendDisabled =
    (selectionType === "collections" && selectedCollections.length === 0) ||
    (selectionType === "databases" && !selectedDatabase) ||
    (selectionType === "csv" && !csvFileId);

  return (
    <Box className={classes.root}>
      <Navbar
        onNewChat={handleNewChat}
        onMenuClick={() => setSidebarOpen(true)}
        showMenuButton={true}
      />
      <Box className={classes.centerContent}>
        <Typography className={classes.title} data-tour="welcome-text">
          Welcome to the Aizatron Cognitive Engine chatbot!
        </Typography>
        <Typography className={classes.description}>
          Search & interact with documents across curated collections.
        </Typography>
        <Box className={classes.boxContainer}>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "-webkit-fill-available",
              alignItems: "center",
              marginBottom: theme.spacing(2),
              gap: theme.spacing(2)
            }}
          >
            <Box style={{ flexGrow: 1 }} data-tour="selection-panel">
              <MainChatSelectionPanel
                selectionType={selectionType}
                handleSelectionTypeChange={(e) =>
                  handleSelectionTypeChange(e, setChatHistory)
                }
                chatId={chatId}
                selectedCollections={selectedCollections}
                handleCollectionChange={(e) =>
                  handleCollectionChange(e, setChatHistory)
                }
                collections={sortedCollections}
                selectedDatabase={selectedDatabase}
                handleDatabaseChange={handleDatabaseChange}
                databases={sortedDatabases}
                loading={loading}
                availableCollections={sortedAvailableCollections}
                setSelectedCollections={setSelectedCollections}
                includeAnalysis={includeAnalysis}
                setIncludeAnalysis={setIncludeAnalysis}
                csvGetRootProps={csvGetRootProps}
                csvGetInputProps={csvGetInputProps}
                csvFile={csvFile}
                handleCsvUpload={handleCsvUpload}
                handleRemoveCsvFile={handleRemoveCsvFile}
                isUploaded={!!csvFileId}
              />
            </Box>
            <CollectionInfoPopover
              selectedCollections={selectedCollections}
              collectionFilesMap={collectionFilesMap}
            />
            {/* <Box style={{ display: "flex", alignItems: "center", minWidth: "fit-content" }}>
              <TokenOutlinedIcon sx={{ color: "#FFC629" }} />
              <Typography
                variant="inherit"
                style={{ color: "white", marginLeft: "8px" }}
              >
                {totalTokensUsed} / {tokenLimit}
              </Typography>
            </Box> */}
          </Box>

          {/* Sidebar Drawer */}
          <ChatSidebar
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            onNewChat={handleNewChat}
            chatSessions={chatSession}
            activeChatId={activeChatId}
            onChatSelection={handleChatSelection}
            selectedChatForMenu={selectedChatForMenu}
            onMouseEnter={setSelectedChatForMenu}
            onMenuOpen={handleMenuOpen}
            onStartQuickChat={() => {
              setSidebarOpen(false);
              navigate("/quick-chat");
            }}
          />

          {/* Chat Container with streaming props */}
          <ChatWindow
            chatHistory={chatHistory}
            setChatHistory={setChatHistory}
            lastMessageRef={lastMessageRef}
            chatContainerRef={chatContainerRef}
            userQuery={userQuery}
            setUserQuery={setUserQuery}
            handleSubmitQuery={handleSubmitQuery}
            loadingRes={loadingRes}
            isChatDisabled={isChatDisabled}
            isSendDisabled={isSendDisabled}
            isStreaming={isStreaming}
            streamingMessageId={streamingMessageId}
            stopStreaming={stopStreaming}
          />

          {/* Retry UI for network errors */}
          {errorMessages.type === ERROR_TYPES.NETWORK &&
            errorMessages.retryable && (
              <RetryMessage
                message={errorMessages.message}
                onRetry={handleRetry}
                onCancel={handleCancelRetry}
                attempt={retryAttempt + 1}
                maxAttempts={3}
              />
            )}

          {/* Generate Visualization Button */}
          {selectionType === "csv" && isVisualization && (
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: theme.spacing(2),
              }}
            >
              <Button
                className={classes.button}
                onClick={handleGenerateVisualization}
                disabled={
                  !chatHistory.some((msg) => msg.sender === "bot") || loadingRes
                }
              >
                {loadingRes ? "Generating" : "Generate Visualisation"}
              </Button>
            </Box>
          )}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          slotProps={{
            paper: {
              style: {
                backgroundColor: "#2a2a2a",
                color: "white",
                fontFamily: "Raleway, sans-serif",
                borderRadius: 8,
              },
            },
          }}
        >
          <MenuItem
            onClick={openRenameModal}
            style={{
              fontFamily: "Raleway, sans-serif",
              color: "white",
              padding: theme.spacing(1, 2),
              fontSize: "0.85em",
              fontWeight: "bold",
            }}
          >
            <EditOutlined
              fontSize="small"
              sx={{ marginRight: theme.spacing(0.25) }}
            />{" "}
            Rename
          </MenuItem>
        </Menu>
        <Modal open={renameModalOpen} onClose={closeRenameModal}>
          <Box className={classes.modal}>
            <Typography variant="h6" className={classes.modalTitle}>
              Rename Chat
            </Typography>
            <TextField
              value={newChatName}
              onChange={(e) => setNewChatName(e.target.value)}
              fullWidth
              variant="outlined"
              inputProps={{
                style: {
                  borderRadius: "9px",
                },
              }}
              sx={{
                input: { color: "#2a2a2a", backgroundColor: "white" },
                fieldset: { borderColor: "#FFC629" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
              }}
            />
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button onClick={handleRenameChat} className={classes.button}>
                Save
              </Button>
              <Button
                onClick={closeRenameModal}
                style={{ marginLeft: theme.spacing(1), color: "white", marginTop: theme.spacing(2) }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>

        <SnackbarAlert
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleCloseSnackbar}
        />
      </Box>
      <Footer />
    </Box>
  );
}
export default MainChat;
